// src/components/Dashboard.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Droplet, Thermometer, Wind, Sun, Cloud, CloudRain, Eye, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SERIES_CHART_DIMENSIONS = {
  width: 640,
  height: 220,
  paddingX: 32,
  paddingY: 28
};

const SERIES_CHART_MODAL_DIMENSIONS = {
  width: 860,
  height: 400,
  paddingX: 40,
  paddingY: 36
};

const SeriesChart = ({
  title,
  ariaLabel,
  series,
  valueKey,
  unitLabel,
  emptyMessage,
  gradientId,
  lineColor,
  areaColor,
  locale,
  valueFormatter,
  isFiltered
}) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const resolvedFormatter = useMemo(
    () => valueFormatter ?? ((value) => value.toFixed(2)),
    [valueFormatter]
  );
  const chartSeries = useMemo(
    () => (series ?? [])
      .map((entry) => ({
        ...entry,
        numericValue: Number(entry[valueKey])
      }))
      .filter((entry) => entry.timestamp && !Number.isNaN(entry.numericValue)),
    [series, valueKey]
  );
  const stats = useMemo(() => {
    if (!chartSeries.length) {
      return null;
    }
    const values = chartSeries.map((entry) => entry.numericValue);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const latest = chartSeries[chartSeries.length - 1];
    return {
      minValue,
      maxValue,
      latest,
      minPoint: chartSeries.find((entry) => entry.numericValue === minValue),
      maxPoint: chartSeries.find((entry) => entry.numericValue === maxValue)
    };
  }, [chartSeries]);
  const formatTooltipDateTime = useCallback((timestamp) => new Date(timestamp).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC'
  }), [locale]);
  const formatAxisDate = useCallback((timestamp) => new Date(timestamp).toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC'
  }), [locale]);

  const getChartWidth = useCallback((dimensions) => {
    if (!chartSeries.length) {
      return dimensions.width;
    }
    const timestamps = chartSeries
      .map((entry) => new Date(entry.timestamp))
      .filter((date) => !Number.isNaN(date.getTime()));
    if (!timestamps.length) {
      return dimensions.width;
    }
    const minTimestamp = Math.min(...timestamps.map((date) => date.getTime()));
    const maxTimestamp = Math.max(...timestamps.map((date) => date.getTime()));
    const dayMs = 24 * 60 * 60 * 1000;
    const dayCount = Math.max(1, Math.ceil((maxTimestamp - minTimestamp) / dayMs) + 1);
    const dayWidth = dimensions.width / 5;
    return Math.max(dimensions.width, dayCount * dayWidth);
  }, [chartSeries]);
  const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

  const getTooltipData = useCallback((dimensions, width) => {
    if (!hoveredPoint) {
      return null;
    }
    const { height, paddingX, paddingY } = dimensions;
    const tooltipWidth = 190;
    const tooltipHeight = 48;
    const offset = 10;
    let x = hoveredPoint.x + offset;
    if (x + tooltipWidth > width - paddingX) {
      x = hoveredPoint.x - tooltipWidth - offset;
    }
    x = clampValue(x, paddingX, width - paddingX - tooltipWidth);
    let y = hoveredPoint.y - tooltipHeight - offset;
    if (y < paddingY) {
      y = hoveredPoint.y + offset;
    }
    y = clampValue(y, paddingY, height - paddingY - tooltipHeight);

    return {
      x,
      y,
      width: tooltipWidth,
      height: tooltipHeight,
      valueLabel: `${resolvedFormatter(hoveredPoint.numericValue)}${unitLabel}`,
      timeLabel: `${formatTooltipDateTime(hoveredPoint.timestamp)} UTC`
    };
  }, [formatTooltipDateTime, hoveredPoint, resolvedFormatter, unitLabel]);

  const buildChartData = useCallback((dimensions) => {
    if (!stats) {
      return null;
    }
    const { height, paddingX, paddingY } = dimensions;
    const width = getChartWidth(dimensions);
    const innerWidth = width - paddingX * 2;
    const innerHeight = height - paddingY * 2;
    const minValue = stats.minValue;
    const maxValue = stats.maxValue;
    const range = maxValue - minValue || 1;
    const getX = (index) => paddingX + (index / Math.max(chartSeries.length - 1, 1)) * innerWidth;
    const getY = (value) => paddingY + (1 - (value - minValue) / range) * innerHeight;
    const yTickCount = 5;
    const yTicks = Array.from({ length: yTickCount }, (_, index) => {
      const value = minValue + (range * index) / Math.max(yTickCount - 1, 1);
      return {
        value,
        y: getY(value),
        label: `${resolvedFormatter(value)}${unitLabel}`
      };
    });
    const xLabels = [];
    const dayIndexMap = new Map();
    chartSeries.forEach((entry, index) => {
      const dayKey = formatAxisDate(entry.timestamp);
      if (!dayIndexMap.has(dayKey)) {
        dayIndexMap.set(dayKey, index);
      }
    });
    dayIndexMap.forEach((index, dayKey) => {
      xLabels.push({
        index,
        x: getX(index),
        label: dayKey
      });
    });
    const points = chartSeries.map((entry, index) => ({
      ...entry,
      x: getX(index),
      y: getY(entry.numericValue)
    }));
    const path = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
    const locatePoint = (target) => points.find((point) => point.id === target?.id);
    const latestPoint = points[points.length - 1];
    const latestLabelWidth = 70;
    const latestLabelOffset = 8;
    let latestLabelX = latestPoint.x + latestLabelOffset;
    if (latestLabelX + latestLabelWidth > width - paddingX) {
      latestLabelX = latestPoint.x - latestLabelWidth - latestLabelOffset;
    }
    latestLabelX = clampValue(latestLabelX, paddingX, width - paddingX - latestLabelWidth);

    return {
      points,
      path,
      xLabels,
      yTicks,
      minPoint: locatePoint(stats.minPoint),
      maxPoint: locatePoint(stats.maxPoint),
      latestPoint,
      latestLabelX,
      latestLabelTextX: latestLabelX + latestLabelWidth / 2,
      latestLabelWidth,
      width
    };
  }, [chartSeries, formatAxisDate, getChartWidth, resolvedFormatter, stats, unitLabel]);

  const chart = useMemo(() => buildChartData(SERIES_CHART_DIMENSIONS), [buildChartData]);
  const modalChart = useMemo(() => buildChartData(SERIES_CHART_MODAL_DIMENSIONS), [buildChartData]);
  const tooltipData = useMemo(() => {
    if (!chart || !hoveredPoint) {
      return null;
    }
    return getTooltipData(SERIES_CHART_DIMENSIONS, chart.width);
  }, [chart, getTooltipData, hoveredPoint]);
  const modalTooltipData = useMemo(() => {
    if (!modalChart || !hoveredPoint) {
      return null;
    }
    return getTooltipData(SERIES_CHART_MODAL_DIMENSIONS, modalChart.width);
  }, [getTooltipData, hoveredPoint, modalChart]);

  const modalTitleId = `${gradientId}-expanded-title`;

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  const renderStats = () => (
    stats && (
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-blue-500"></span>
          Min {resolvedFormatter(stats.minValue)}{unitLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-500"></span>
          Max {resolvedFormatter(stats.maxValue)}{unitLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
          Güncel {resolvedFormatter(stats.latest.numericValue)}{unitLabel}
        </span>
      </div>
    )
  );

  const renderChartContent = (dimensions, chartData, tooltipInfo, svgHeightClass, ariaLabelOverride) => {
    if (!chartData) {
      return (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-slate-600">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="relative overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartData.width} ${dimensions.height}`}
          className={`${svgHeightClass} w-full`}
          style={{ minWidth: chartData.width }}
          role="img"
          aria-label={ariaLabelOverride ?? ariaLabel}
        >
          <defs>
            <linearGradient id={`${gradientId}-line`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={lineColor[0]} />
              <stop offset="100%" stopColor={lineColor[1]} />
            </linearGradient>
            <linearGradient id={`${gradientId}-area`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaColor} stopOpacity="0.4" />
              <stop offset="100%" stopColor={areaColor} stopOpacity="0" />
            </linearGradient>
          </defs>
          <rect
            x="0"
            y="0"
            width={chartData.width}
            height={dimensions.height}
            rx="16"
            fill="#f8fafc"
          />
          <g stroke="#e2e8f0" strokeDasharray="3 3">
            {chartData.yTicks.map((tick) => (
              <line
                key={`y-grid-${tick.value}`}
                x1={dimensions.paddingX}
                x2={chartData.width - dimensions.paddingX}
                y1={tick.y}
                y2={tick.y}
              />
            ))}
            {chartData.xLabels.map((label) => (
              <line
                key={`x-grid-${label.index}`}
                x1={label.x}
                x2={label.x}
                y1={dimensions.paddingY}
                y2={dimensions.height - dimensions.paddingY}
              />
            ))}
          </g>
          <path
            d={`${chartData.path} L ${chartData.points[chartData.points.length - 1].x} ${dimensions.height - dimensions.paddingY} L ${chartData.points[0].x} ${dimensions.height - dimensions.paddingY} Z`}
            fill={`url(#${gradientId}-area)`}
          />
          <path d={chartData.path} fill="none" stroke={`url(#${gradientId}-line)`} strokeWidth="3" />
          <g fontSize="10" fill="#64748b">
            {chartData.yTicks.map((tick) => (
              <text
                key={`y-label-${tick.value}`}
                x={dimensions.paddingX - 8}
                y={tick.y}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {tick.label}
              </text>
            ))}
            {chartData.xLabels.map((label) => (
              <text
                key={`x-label-${label.index}`}
                x={label.x}
                y={dimensions.height - dimensions.paddingY + 12}
                textAnchor="middle"
                dominantBaseline="hanging"
              >
                {label.label}
              </text>
            ))}
          </g>
          {chartData.points.map((point) => (
            <circle
              key={point.id}
              cx={point.x}
              cy={point.y}
              r={hoveredPoint?.id === point.id ? '4' : '2.5'}
              fill="#1e293b"
              opacity={hoveredPoint?.id === point.id ? '0.9' : '0.5'}
              onMouseEnter={() => setHoveredPoint(point)}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              <title>
                {`${resolvedFormatter(point.numericValue)}${unitLabel} • ${formatTooltipDateTime(point.timestamp)} UTC`}
              </title>
            </circle>
          ))}
          {tooltipInfo && (
            <g>
              <rect
                x={tooltipInfo.x}
                y={tooltipInfo.y}
                width={tooltipInfo.width}
                height={tooltipInfo.height}
                rx="8"
                fill="#0f172a"
                opacity="0.9"
              />
              <text x={tooltipInfo.x + 8} y={tooltipInfo.y + 16} fontSize="10" fill="#f8fafc">
                {tooltipInfo.valueLabel}
              </text>
              <text x={tooltipInfo.x + 8} y={tooltipInfo.y + 34} fontSize="10" fill="#e2e8f0">
                {tooltipInfo.timeLabel}
              </text>
            </g>
          )}
          {chartData.minPoint && (
            <>
              <circle cx={chartData.minPoint.x} cy={chartData.minPoint.y} r="6" fill="#3b82f6" />
              <text
                x={chartData.minPoint.x}
                y={chartData.minPoint.y - 12}
                textAnchor="middle"
                fontSize="10"
                fill="#1e3a8a"
              >
                Min
              </text>
            </>
          )}
          {chartData.maxPoint && (
            <>
              <circle cx={chartData.maxPoint.x} cy={chartData.maxPoint.y} r="6" fill="#f43f5e" />
              <text
                x={chartData.maxPoint.x}
                y={chartData.maxPoint.y - 12}
                textAnchor="middle"
                fontSize="10"
                fill="#9f1239"
              >
                Max
              </text>
            </>
          )}
          {chartData.latestPoint && (
            <>
              <circle cx={chartData.latestPoint.x} cy={chartData.latestPoint.y} r="6" fill="#10b981" />
              <rect
                x={chartData.latestLabelX}
                y={chartData.latestPoint.y - 18}
                width={chartData.latestLabelWidth}
                height="20"
                rx="8"
                fill="#0f172a"
                opacity="0.85"
              />
              <text
                x={chartData.latestLabelTextX}
                y={chartData.latestPoint.y - 4}
                textAnchor="middle"
                fontSize="10"
                fill="#f8fafc"
              >
                {resolvedFormatter(chartSeries[chartSeries.length - 1].numericValue)}{unitLabel}
              </text>
            </>
          )}
        </svg>
        <div className="mt-3 flex flex-wrap items-center justify-between text-xs text-slate-500">
          <span>UTC</span>
          <span>
            {chartSeries.length} ölçüm{isFiltered ? ' (filtreli)' : ''}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        <div className="flex flex-wrap items-center gap-3">
          {renderStats()}
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
            aria-haspopup="dialog"
            aria-expanded={isExpanded}
            aria-label={`${title} grafiğini büyüt`}
            title={`${title} grafiğini büyüt`}
          >
            <Search size={16} />
          </button>
        </div>
      </div>

      {renderChartContent(
        SERIES_CHART_DIMENSIONS,
        chart,
        tooltipData,
        'h-[260px]'
      )}

      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-8"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsExpanded(false);
            }
          }}
        >
          <div
            className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <div>
                <h3 id={modalTitleId} className="text-xl font-bold text-slate-900">{title}</h3>
                <p className="text-xs text-slate-500">Geniş görünüm</p>
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                aria-label="Grafiği kapat"
              >
                Kapat
              </button>
            </div>
            {renderStats()}
            <div className="mt-4">
              {renderChartContent(
                SERIES_CHART_MODAL_DIMENSIONS,
                modalChart,
                modalTooltipData,
                'h-[420px]',
                `${ariaLabel} geniş görünüm`
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({
  data,
  setActiveView,
  folderDeviceCounts,
  unitRowCount,
  temperatureSeries,
  humiditySeries,
  illuminationSeries,
  deviceIds,
  selectedDeviceId,
  onSelectDevice
}) => {
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [appliedDateRange, setAppliedDateRange] = useState({ start: null, end: null });
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const {
    turfBox: turfBoxDeviceCount = 0,
    unite: unitCount = 0,
    isitma: heatingCount = 0,
    sulama: irrigationCount = 0
  } = folderDeviceCounts ?? {};
  const selectedDeviceEntries = useMemo(() => {
    if (!selectedDeviceId) {
      return data;
    }
    return data.filter((entry) => String(entry.device) === String(selectedDeviceId));
  }, [data, selectedDeviceId]);
  const resolvedUnitCount = unitRowCount ?? unitCount;
  const heatingStatus = heatingCount > 0 ? t('dashboard.online') : t('dashboard.offline');
  const heatingStatusBadgeClasses = heatingCount > 0
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : 'bg-rose-50 text-rose-700 ring-rose-200';
  const formatUtcDateTime = useCallback((date) => {
    if (!date) {
      return t('dashboard.noData');
    }
    const resolvedDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(resolvedDate.getTime())) {
      return t('dashboard.noData');
    }
    return resolvedDate.toLocaleString(locale, { timeZone: 'UTC' });
  }, [locale, t]);
  const buildUtcDate = useCallback((dateValue, isEndDate = false) => {
    if (!dateValue) {
      return null;
    }
    const suffix = isEndDate ? 'T23:59:59.999Z' : 'T00:00:00Z';
    const resolvedDate = new Date(`${dateValue}${suffix}`);
    if (Number.isNaN(resolvedDate.getTime())) {
      return null;
    }
    return resolvedDate;
  }, []);
  const applyDateRange = useCallback(() => {
    const startDate = buildUtcDate(dateRange.start, false);
    const endDate = buildUtcDate(dateRange.end, true);
    let resolvedStart = startDate;
    let resolvedEnd = endDate;
    if (resolvedStart && resolvedEnd && resolvedStart > resolvedEnd) {
      [resolvedStart, resolvedEnd] = [resolvedEnd, resolvedStart];
    }
    setAppliedDateRange({ start: resolvedStart, end: resolvedEnd });
  }, [buildUtcDate, dateRange.end, dateRange.start]);
  const clearDateRange = useCallback(() => {
    setDateRange({ start: '', end: '' });
    setAppliedDateRange({ start: null, end: null });
  }, []);
  const filterSeriesByDate = useCallback((series) => {
    if (!series?.length) {
      return [];
    }
    if (!appliedDateRange.start && !appliedDateRange.end) {
      return series;
    }
    return series.filter((entry) => {
      const resolvedDate = new Date(entry.timestamp);
      if (Number.isNaN(resolvedDate.getTime())) {
        return false;
      }
      if (appliedDateRange.start && resolvedDate < appliedDateRange.start) {
        return false;
      }
      if (appliedDateRange.end && resolvedDate > appliedDateRange.end) {
        return false;
      }
      return true;
    });
  }, [appliedDateRange.end, appliedDateRange.start]);
  const filteredTemperatureSeries = useMemo(
    () => filterSeriesByDate(temperatureSeries ?? []),
    [filterSeriesByDate, temperatureSeries]
  );
  const filteredHumiditySeries = useMemo(
    () => filterSeriesByDate(humiditySeries ?? []),
    [filterSeriesByDate, humiditySeries]
  );
  const filteredIlluminationSeries = useMemo(
    () => filterSeriesByDate(illuminationSeries ?? []),
    [filterSeriesByDate, illuminationSeries]
  );
  const isDateRangeFiltered = Boolean(appliedDateRange.start || appliedDateRange.end);
  const deviceSeriesSummary = useMemo(() => {
    const buildSummary = (entries, valueAccessor, dateAccessor) => {
      if (!entries.length) {
        return {
          avg: null,
          latestDate: null
        };
      }
      const values = entries
        .map((entry) => Number(valueAccessor(entry)))
        .filter((value) => !Number.isNaN(value));
      const avg = values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
      const latestDate = entries
        .map((entry) => dateAccessor(entry))
        .map((value) => (value instanceof Date ? value : new Date(value)))
        .filter((value) => !Number.isNaN(value.getTime()))
        .reduce((latest, value) => (!latest || value > latest ? value : latest), null);
      return {
        avg,
        latestDate
      };
    };

    return {
      temperature: buildSummary(
        temperatureSeries ?? [],
        (entry) => entry.temperatureC,
        (entry) => entry.timestamp
      ),
      moisture: buildSummary(
        selectedDeviceEntries,
        (entry) => entry.soilMoisture,
        (entry) => entry.created_at
      ),
      other: buildSummary(
        illuminationSeries ?? [],
        (entry) => entry.illumination,
        (entry) => entry.timestamp
      )
    };
  }, [illuminationSeries, selectedDeviceEntries, temperatureSeries]);

  // Hava durumu verilerini çek
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setWeatherLoading(true);
        
        // OpenWeatherMap API (ücretsiz)
        const API_KEY = 'demo'; // Demo mod için
        const city = 'Kocaeli,TR';
        
        // Gerçek API kullanımı için:
        // const response = await fetch(`https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${API_KEY}&units=metric&lang=tr`);
        // const data = await response.json();
        
        // Demo verisi (API key olmadan test için)
        // Gerçek uygulamada yukarıdaki API çağrısını kullanın
        const mockData = {
          main: {
            temp: 12.5,
            feels_like: 11.2,
            humidity: 72,
            pressure: 1013
          },
          weather: [
            {
              main: 'Clouds',
              description: 'partly cloudy',
              descriptionKey: 'dashboard.weatherPartlyCloudy',
              icon: '02d'
            }
          ],
          wind: {
            speed: 3.5
          },
          visibility: 10000,
          name: 'Kocaeli',
          sys: {
            country: 'TR'
          }
        };
        
        setWeather(mockData);
        setWeatherLoading(false);
      } catch (error) {
        console.error('Hava durumu alınamadı:', error);
        setWeatherLoading(false);
      }
    };

    fetchWeather();
    // Her 10 dakikada bir güncelle
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);
  // Hava durumu ikonu
  const getWeatherIcon = (main) => {
    switch (main?.toLowerCase()) {
      case 'clear': return <Sun className="text-yellow-500" size={48} />;
      case 'clouds': return <Cloud className="text-gray-500" size={48} />;
      case 'rain': return <CloudRain className="text-blue-500" size={48} />;
      default: return <Cloud className="text-gray-400" size={48} />;
    }
  };

  // İstatistikleri hesapla
  const stats = useMemo(() => {
    if (data.length === 0) return null;

    const devices = new Set(data.map(d => d.device)).size;
    const locations = new Set(data.map(d => d.location)).size;

    // Ortalamalar
    const avgAirTemp = data.reduce((sum, d) => sum + parseFloat(d.airTempC || 0), 0) / data.length;
    const avgSoilTemp = data.reduce((sum, d) => sum + parseFloat(d.soilTempC || 0), 0) / data.length;
    const avgHumidity = data.reduce((sum, d) => sum + parseFloat(d.humidity || 0), 0) / data.length;
    const avgSoilMoisture = data.reduce((sum, d) => sum + parseFloat(d.soilMoisture || 0), 0) / data.length;
    const avgPH = data.reduce((sum, d) => sum + parseFloat(d.soilPH || 0), 0) / data.length;
    const avgEC = data.reduce((sum, d) => sum + parseFloat(d.soilEC || 0), 0) / data.length;
    const avgCO2 = data.reduce((sum, d) => sum + parseFloat(d.co2 || 0), 0) / data.length;
    const avgWind = data.reduce((sum, d) => sum + parseFloat(d.windSpeed || 0), 0) / data.length;
    const avgLux = data.reduce((sum, d) => sum + parseFloat(d.lux || 0), 0) / data.length;

    // Min/Max değerler
    const minSoilMoisture = Math.min(...data.map(d => parseFloat(d.soilMoisture)));
    const maxSoilMoisture = Math.max(...data.map(d => parseFloat(d.soilMoisture)));
    const minAirTemp = Math.min(...data.map(d => parseFloat(d.airTempC)));
    const maxAirTemp = Math.max(...data.map(d => parseFloat(d.airTempC)));

    // Uyarılar
    const lowMoisture = data.filter(d => parseFloat(d.soilMoisture) < 20).length;
    const highTemp = data.filter(d => parseFloat(d.soilTempC) > 20).length;
    const lowPH = data.filter(d => parseFloat(d.soilPH) < 6.0).length;
    const highPH = data.filter(d => parseFloat(d.soilPH) > 7.5).length;

    // Cihazlara göre dağılım
    const deviceStats = {};
    data.forEach(d => {
      if (!deviceStats[d.device]) {
        deviceStats[d.device] = { count: 0, avgMoisture: 0, totalMoisture: 0 };
      }
      deviceStats[d.device].count++;
      deviceStats[d.device].totalMoisture += parseFloat(d.soilMoisture);
    });

    Object.keys(deviceStats).forEach(device => {
      deviceStats[device].avgMoisture = (deviceStats[device].totalMoisture / deviceStats[device].count).toFixed(1);
    });

    // Zone'lara göre dağılım
    const zoneStats = {};
    data.forEach(d => {
      if (!zoneStats[d.location]) {
        zoneStats[d.location] = { count: 0, avgTemp: 0, totalTemp: 0 };
      }
      zoneStats[d.location].count++;
      zoneStats[d.location].totalTemp += parseFloat(d.soilTempC);
    });

    Object.keys(zoneStats).forEach(zone => {
      zoneStats[zone].avgTemp = (zoneStats[zone].totalTemp / zoneStats[zone].count).toFixed(1);
    });

    return {
      totalRecords: data.length,
      devices,
      locations,
      avgAirTemp: avgAirTemp.toFixed(1),
      avgSoilTemp: avgSoilTemp.toFixed(1),
      avgHumidity: avgHumidity.toFixed(1),
      avgSoilMoisture: avgSoilMoisture.toFixed(1),
      avgPH: avgPH.toFixed(1),
      avgEC: avgEC.toFixed(2),
      avgCO2: avgCO2.toFixed(0),
      avgWind: avgWind.toFixed(1),
      avgLux: avgLux.toFixed(0),
      minSoilMoisture: minSoilMoisture.toFixed(1),
      maxSoilMoisture: maxSoilMoisture.toFixed(1),
      minAirTemp: minAirTemp.toFixed(1),
      maxAirTemp: maxAirTemp.toFixed(1),
      alerts: {
        lowMoisture,
        highTemp,
        lowPH,
        highPH,
        total: lowMoisture + highTemp + lowPH + highPH
      },
      deviceStats,
      zoneStats
    };
  }, [data]);

  const weatherDescription = weather?.weather?.[0]?.descriptionKey
    ? t(weather.weather[0].descriptionKey)
    : weather?.weather?.[0]?.description;

  if (!stats) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
        <p className="text-slate-700">{t('dashboard.loading')}</p>
      </div>
    );
  }

  const clickableCardClasses = "w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 cursor-pointer flex flex-col justify-between min-h-[180px]";

  return (
    <div className="space-y-6">
      {/* Header */}
     
      {/* Weather Widget */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold text-slate-900">{t('dashboard.liveWeather')}</h3>
              <span className="text-sm text-slate-600">{t('dashboard.location')}</span>
            </div>
            {weatherLoading ? (
              <p className="text-sm text-slate-600">{t('dashboard.loading')}</p>
            ) : weather ? (
              <div className="space-y-2">
                <div className="flex items-baseline gap-3">
                  <div className="text-5xl font-bold text-slate-900">{Math.round(weather.main.temp)}{t('units.celsius')}</div>
                  <div className="text-lg text-slate-700">
                    {t('dashboard.feelsLike')}: {Math.round(weather.main.feels_like)}{t('units.celsius')}
                  </div>
                </div>
                <p className="text-lg text-slate-700 capitalize">{weatherDescription}</p>
              </div>
            ) : (
              <p className="text-sm text-slate-600">{t('dashboard.noData')}</p>
            )}
          </div>

          {weather && (
            <div className="flex flex-col items-end gap-4">
              <div className="bg-gray-100 rounded-full p-4">
                {getWeatherIcon(weather.weather[0].main)}
              </div>
              <div className="text-right space-y-1">
                <div className="flex items-center gap-2 justify-end">
                  <Droplet size={18} className="text-slate-600" />
                  <span className="text-sm text-slate-700">{t('dashboard.humidity')}: {weather.main.humidity}%</span>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Wind size={18} className="text-slate-600" />
                  <span className="text-sm text-slate-700">{t('dashboard.wind')}: {weather.wind.speed} {t('units.ms')}</span>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Eye size={18} className="text-slate-600" />
                  <span className="text-sm text-slate-700">{t('dashboard.visibility')}: {(weather.visibility / 1000).toFixed(1)} {t('units.km')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {weather && (
          <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-slate-600">
            {t('dashboard.lastUpdate')}: {new Date().toLocaleString(locale)} • {t('dashboard.weatherSource')}
          </div>
        )}
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <button
          type="button"
          onClick={() => setActiveView('table')}
            className={clickableCardClasses}
        >
          <div>
            <div className="text-sm text-slate-600 mb-2">{t('dashboard.turfBoxSummary')}</div>
            <div className="text-3xl font-bold text-slate-900">{t('dashboard.turfBoxActive', { count: 1 })}</div>
            <div className="text-xs text-slate-600 mt-3">
              {t('dashboard.turfBoxInactiveTotal', { inactive: 1, total: 2 })}
            </div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline">
            {t('dashboard.viewDetails')}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('units')}
          className={clickableCardClasses}
        >
          <div>
            <div className="text-sm text-slate-600 mb-2">{t('dashboard.unitsSummary')}</div>
            <div className="text-3xl font-bold text-slate-900">{t('dashboard.unitsCount', { count: 5 })}</div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline">
            {t('dashboard.viewDetails')}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('irrigation')}
          className={clickableCardClasses}
        >
          <div>
            <div className="text-sm text-slate-600 mb-2">{t('dashboard.irrigationSummary')}</div>
            <div className="text-3xl font-bold text-slate-900">{t('dashboard.irrigationDuration', { minutes: 45 })}</div>
            <div className="text-xs text-slate-600 mt-3">{t('dashboard.irrigationTodayTotal')}</div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline">
            {t('dashboard.viewDetails')}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveView('heating')}
          className={clickableCardClasses}
        >
          <div>
            <div className="text-sm text-slate-600 mb-2">{t('dashboard.heatingSummary')}</div>
            <div className="text-3xl font-bold text-slate-900">{t('dashboard.offline')}</div>
          </div>
          <span className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline">
            {t('dashboard.viewDetails')}
          </span>
        </button>
      </div>

      {/* Environmental Conditions */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-slate-600" htmlFor="device-select">
              {t('dashboard.deviceLabel')}
            </label>
            <select
              id="device-select"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              value={selectedDeviceId ?? ''}
              onChange={(event) => onSelectDevice?.(event.target.value)}
            >
              <option value="" disabled>
                {t('dashboard.selectDevice')}
              </option>
              {(deviceIds ?? []).map((deviceId) => (
                <option key={deviceId} value={deviceId}>
                  {deviceId}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{t('dashboard.chartDateRange')}</span>
              <div>{t('dashboard.chartDateRangeHint')}</div>
            </div>
            <label className="flex flex-col text-xs font-semibold text-slate-600">
              {t('dashboard.startDate')}
              <input
                type="date"
                value={dateRange.start}
                onChange={(event) => setDateRange((prev) => ({ ...prev, start: event.target.value }))}
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </label>
            <label className="flex flex-col text-xs font-semibold text-slate-600">
              {t('dashboard.endDate')}
              <input
                type="date"
                value={dateRange.end}
                onChange={(event) => setDateRange((prev) => ({ ...prev, end: event.target.value }))}
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={applyDateRange}
                className="inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
              >
                {t('dashboard.apply')}
              </button>
              <button
                type="button"
                onClick={clearDateRange}
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
              >
                {t('dashboard.clear')}
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Temperature Card */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Thermometer className="text-orange-600" size={24} />
              <h4 className="font-bold text-slate-900">{t('dashboard.temperature')}</h4>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <div>
                Son Veri Tarihi:{' '}
                <span className="font-semibold text-slate-900">
                  {formatUtcDateTime(deviceSeriesSummary.temperature.latestDate)}
                </span>
              </div>
              <div>
                Ortalama Veri:{' '}
                <span className="font-semibold text-slate-900">
                  {deviceSeriesSummary.temperature.avg === null
                    ? t('dashboard.noData')
                    : `${deviceSeriesSummary.temperature.avg.toFixed(2)}${t('units.celsius')}`}
                </span>
              </div>
            </div>
          </div>

          {/* Moisture Card */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Droplet className="text-blue-600" size={24} />
              <h4 className="font-bold text-slate-900">{t('dashboard.moisture')}</h4>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <div>
                Son Veri Tarihi:{' '}
                <span className="font-semibold text-slate-900">
                  {formatUtcDateTime(deviceSeriesSummary.moisture.latestDate)}
                </span>
              </div>
              <div>
                Ortalama Veri:{' '}
                <span className="font-semibold text-slate-900">
                  {deviceSeriesSummary.moisture.avg === null
                    ? t('dashboard.noData')
                    : `${deviceSeriesSummary.moisture.avg.toFixed(1)}%`}
                </span>
              </div>
            </div>
          </div>

          {/* Illumination */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Sun className="text-purple-600" size={24} />
              <h4 className="font-bold text-slate-900">{t('dashboard.illumination')}</h4>
            </div>
            <div className="space-y-2 text-sm text-slate-600">
              <div>
                Son Veri Tarihi:{' '}
                <span className="font-semibold text-slate-900">
                  {formatUtcDateTime(deviceSeriesSummary.other.latestDate)}
                </span>
              </div>
              <div>
                Ortalama Veri:{' '}
                <span className="font-semibold text-slate-900">
                  {deviceSeriesSummary.other.avg === null
                    ? t('dashboard.noData')
                    : `${deviceSeriesSummary.other.avg.toFixed(0)} ${t('units.lux')}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SeriesChart
        title={t('dashboard.temperature')}
        ariaLabel="Temperature line chart"
        series={filteredTemperatureSeries}
        valueKey="temperatureC"
        unitLabel={t('units.celsius')}
        emptyMessage="Sıcaklık serisi bulunamadı."
        gradientId="temp-series"
        lineColor={['#3b82f6', '#0f766e']}
        areaColor="#93c5fd"
        locale={locale}
        valueFormatter={(value) => value.toFixed(2)}
        isFiltered={isDateRangeFiltered}
      />

      <SeriesChart
        title={t('dashboard.humidity')}
        ariaLabel="Humidity line chart"
        series={filteredHumiditySeries}
        valueKey="humidity"
        unitLabel={t('units.percent')}
        emptyMessage="Nem serisi bulunamadı."
        gradientId="humidity-series"
        lineColor={['#38bdf8', '#0ea5e9']}
        areaColor="#bae6fd"
        locale={locale}
        valueFormatter={(value) => value.toFixed(1)}
        isFiltered={isDateRangeFiltered}
      />

      <SeriesChart
        title={t('table.detailHeaders.lux')}
        ariaLabel="Illumination line chart"
        series={filteredIlluminationSeries}
        valueKey="illumination"
        unitLabel={` ${t('units.lux')}`}
        emptyMessage="Aydınlatma serisi bulunamadı."
        gradientId="illumination-series"
        lineColor={['#f59e0b', '#ea580c']}
        areaColor="#fde68a"
        locale={locale}
        valueFormatter={(value) => value.toFixed(0)}
        isFiltered={isDateRangeFiltered}
      />

     

      {/* Device Distribution */}
      {/*
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">{t('dashboard.deviceDistribution')}</h3>
          <div className="space-y-3">
            {Object.entries(stats.deviceStats).map(([device, data]) => (
              <div key={device} className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">{device}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(parseFloat(data.avgMoisture) / 40) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-slate-900 w-12 text-right">
                    {data.avgMoisture}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-shadow duration-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">{t('dashboard.zoneDistribution')}</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {Object.entries(stats.zoneStats)
              .sort((a, b) => parseFloat(b[1].avgTemp) - parseFloat(a[1].avgTemp))
              .slice(0, 10)
              .map(([zone, data]) => (
                <div key={zone} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">{zone}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-orange-600 h-1.5 rounded-full"
                        style={{ width: `${(parseFloat(data.avgTemp) / 25) * 100}%` }}
                      ></div>
                    </div>
                    <span className="font-bold text-slate-900 w-10 text-right">
                      {data.avgTemp}°C
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

export default Dashboard;
